import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApplicationUserService } from 'src/app/core/services/application-user.service';
import { Role } from 'src/app/core/models/role.enum';
import { RootAccountBalanceDTO } from 'src/app/models/account/root-account-balance-dto';
import { Currency } from 'src/app/models/enums/currency';
import { CurrencySubTypes } from 'src/app/models/enums/currency-sub-types';
import { AccountTransactionService } from 'src/app/services/account-transaction.service';
import { currencyIcon } from '../balance-kpi-card';

/** Bir alt türün stok satırı (ör. "22 Ayar", "Yeni Çeyrek"). */
interface StockRow {
  subType: CurrencySubTypes;
  /** Kasa ekranındaki "bakiye" — birimi alt türe göre gr ya da adet (currencyWeight). */
  quantity: number;
  /** Has gram karşılığı. */
  has: number;
}

interface StockCard {
  currency: Currency;
  title: string;
  icon: string;
  /** Bootstrap renk adı (simge zemini). */
  tone: string;
  totalHas: number;
  rows: StockRow[];
}

/** Kartların sırası ve başlıkları. */
const STOCK_CURRENCIES: { currency: Currency; title: string; tone: string }[] = [
  { currency: Currency.Gold, title: 'Altın Stoku', tone: 'warning' },
  { currency: Currency.Accessories, title: 'Sarrafiye Stoku', tone: 'primary' },
  { currency: Currency.OtherAccessories, title: 'Sarrafiye (Diğer) Stoku', tone: 'info' },
];

/**
 * Altın, sarrafiye ve diğer sarrafiye stoklarını alt tür bazında gösterir (ana sayfa).
 *
 * Kaynak, Kasa Bakiyeleri ekranının kullandığı `AccountTransaction/get-case-balances` ucudur:
 * şubenin kasa bakiyeleri + satılmamış ürünler, para birimi başına alt tür kırılımıyla.
 * Müşteri bakiyeleri stok değildir, dahil edilmez (üstteki bakiye kartlarında var).
 *
 * `scope="branch"` seçili şubeyi, `scope="all"` kullanıcının yetkili olduğu tüm şubeleri
 * gösterir — uç şube bazında olduğu için her şube ayrı istenir ve alt tür bazında toplanır.
 */
@Component({
  selector: 'app-stock-balance',
  templateUrl: './stock-balance.component.html',
})
export class StockBalanceComponent implements OnInit, OnDestroy {
  @Input() scope: 'branch' | 'all' = 'branch';

  readonly Currency = Currency;
  readonly GoldNew = Currency.GoldNew;

  hasUserRole = false;
  loading = true;
  error = false;
  cards: StockCard[] = [];

  private refreshSubscription?: Subscription;

  constructor(
    private applicationUserService: ApplicationUserService,
    private accountTransactionService: AccountTransactionService,
  ) {}

  ngOnInit(): void {
    const user = this.applicationUserService.currentUserValue;
    this.hasUserRole = !!user?.roles?.includes(Role.User);
    if (!this.hasUserRole) {
      this.loading = false;
      return;
    }

    this.load();
    // Kasa hareketi eklenince (bakiye kartlarındaki "+" vb.) stok da güncellensin.
    this.refreshSubscription = this.accountTransactionService.refreshIndex.subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  get scopeLabel(): string {
    if (this.scope === 'all') {
      const count = this.applicationUserService.currentUserValue?.branchList?.length || 0;
      return `Tüm şubeler (${count})`;
    }
    return this.applicationUserService.currentBranchValue?.name || 'Şube';
  }

  private branchIds(): number[] {
    if (this.scope === 'all') {
      return (this.applicationUserService.currentUserValue?.branchList || [])
        .map((branch) => branch.id)
        .filter((id): id is number => id != null);
    }
    const branchId = this.applicationUserService.currentBranchValue?.id;
    return branchId != null ? [branchId] : [];
  }

  load(): void {
    const branchIds = this.branchIds();
    if (!branchIds.length) {
      this.cards = this.buildCards([]);
      this.loading = false;
      return;
    }

    this.loading = true;
    this.error = false;
    const endDate = new Date();

    Promise.all(
      branchIds.map((branchId) =>
        this.accountTransactionService.getCaseBalances({ branchId, endDate }).toPromise()
          .then((res) => (res?.isSuccess ? res.data?.balanceList || [] : Promise.reject()))
      )
    )
      .then((lists) => {
        this.cards = this.buildCards(lists);
      })
      .catch(() => {
        // Tek bir şube bile okunamazsa toplam yanıltıcı olur; kısmi sonuç gösterilmez.
        this.error = true;
        this.cards = [];
      })
      .finally(() => (this.loading = false));
  }

  /** Şube listelerini para birimi + alt tür bazında toplayıp kartları üretir. */
  private buildCards(lists: RootAccountBalanceDTO[][]): StockCard[] {
    return STOCK_CURRENCIES.map(({ currency, title, tone }) => {
      const bySubType = new Map<CurrencySubTypes, StockRow>();

      lists.forEach((list) =>
        (list || [])
          .filter((balance) => balance.currency === currency)
          .forEach((balance) =>
            (balance.subTypeBalances || []).forEach((sub) => {
              if (sub.subType == null) return;
              const row = bySubType.get(sub.subType) || { subType: sub.subType, quantity: 0, has: 0 };
              row.quantity += sub.balance || 0;
              row.has += sub.balanceHas || 0;
              bySubType.set(sub.subType, row);
            })
          )
      );

      // Kayan nokta artığı (1e-12 gibi) yüzünden sıfır satır görünmesin.
      const rows = Array.from(bySubType.values())
        .map((row) => ({ ...row, quantity: round(row.quantity), has: round(row.has) }))
        .filter((row) => row.quantity !== 0 || row.has !== 0)
        .sort((a, b) => a.subType - b.subType);

      return {
        currency,
        title,
        icon: currencyIcon(currency),
        tone,
        totalHas: round(rows.reduce((sum, row) => sum + row.has, 0)),
        rows,
      };
    });
  }

  trackByCurrency(_: number, card: StockCard): Currency {
    return card.currency;
  }

  trackBySubType(_: number, row: StockRow): CurrencySubTypes {
    return row.subType;
  }
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
